import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Search as SearchIcon, MyLocation as MyLocationIcon, Clear as ClearIcon } from '@mui/icons-material';
import { Dream, DreamType, DreamPriority, UpdateDreamData } from '../types';
import { dreamsService } from '../services/dreamsService';
import { US_STATES } from '../utils/usStates';

interface EditDreamDialogProps {
  open: boolean;
  dream: Dream | null;
  onClose: () => void;
  onDreamUpdated: (dream: Dream) => void;
}

const EditDreamDialog: React.FC<EditDreamDialogProps> = ({
  open,
  dream,
  onClose,
  onDreamUpdated,
}) => {
  const [formData, setFormData] = useState<UpdateDreamData>({
    title: '',
    description: '',
    dream_type: 'destination',
    priority: 'medium',
    notes: '',
    estimated_budget: undefined,
    best_time_to_visit: '',
  });
  const [locationSearchValue, setLocationSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (dream) {
      setFormData({
        title: dream.title,
        description: dream.description || '',
        dream_type: dream.dream_type,
        priority: dream.priority,
        notes: dream.notes || '',
        estimated_budget: dream.estimated_budget || undefined,
        best_time_to_visit: dream.best_time_to_visit || '',
      });
      setLocationSearchValue(dream.location_name || '');
    }
  }, [dream]);

  const handleLocationChange = (value: string) => {
    setLocationSearchValue(value);
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`;
          setLocationSearchValue(location);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setError('Unable to get your current location. Please check location permissions.');
        }
      );
    }
  };

  const handleSubmit = async () => {
    if (!dream) return;

    try {
      setLoading(true);
      setError(null);

      const updateData: UpdateDreamData = {
        ...formData,
      };

      // Include location data if location search value is provided
      if (locationSearchValue.trim() && locationSearchValue !== dream.location_name) {
        updateData.location_name = locationSearchValue.trim();
      }

      await dreamsService.updateDream(dream.id, updateData);
      
      // Create updated dream object for callback
      const updatedDream: Dream = {
        ...dream,
        ...updateData,
        updated_at: new Date().toISOString(),
      };
      
      onDreamUpdated(updatedDream);
      onClose();
    } catch (error) {
      console.error('Failed to update dream:', error);
      setError(error instanceof Error ? error.message : 'Failed to update dream');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      dream_type: 'destination',
      priority: 'medium',
      notes: '',
      estimated_budget: undefined,
      best_time_to_visit: '',
    });
    setLocationSearchValue('');
    setError(null);
    onClose();
  };

  if (!dream) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Dream</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          {/* City and State fields for enhanced search */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="City"
              placeholder="e.g., Ormond Beach"
              helperText="City to search in"
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>State</InputLabel>
              <Select
                label="State"
              >
                {US_STATES.map(state => (
                  <MenuItem key={state.value} value={state.value}>
                    {state.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            fullWidth
            label="Location"
            value={locationSearchValue}
            onChange={(e) => handleLocationChange(e.target.value)}
            placeholder="Enter location name"
            helperText="Update the location for this dream"
            margin="dense"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleUseCurrentLocation}
                    title="Use current location"
                  >
                    <MyLocationIcon />
                  </IconButton>
                  {locationSearchValue && (
                    <IconButton
                      size="small"
                      onClick={() => setLocationSearchValue('')}
                      title="Clear search"
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            margin="dense"
            required
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            margin="dense"
            multiline
            rows={3}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <FormControl fullWidth margin="dense">
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.dream_type}
                label="Type"
                onChange={(e) => setFormData(prev => ({ ...prev, dream_type: e.target.value as DreamType }))}
              >
                <MenuItem value="destination">Destination</MenuItem>
                <MenuItem value="attraction">Attraction</MenuItem>
                <MenuItem value="restaurant">Restaurant</MenuItem>
                <MenuItem value="accommodation">Accommodation</MenuItem>
                <MenuItem value="activity">Activity</MenuItem>
                <MenuItem value="brewery">Brewery</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="dense">
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as DreamPriority }))}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TextField
            fullWidth
            label="Notes"
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            margin="dense"
            multiline
            rows={3}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Estimated Budget"
              type="number"
              value={formData.estimated_budget || ''}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                estimated_budget: e.target.value ? parseFloat(e.target.value) : undefined 
              }))}
              margin="dense"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
              }}
            />

            <TextField
              fullWidth
              label="Best Time to Visit"
              value={formData.best_time_to_visit}
              onChange={(e) => setFormData(prev => ({ ...prev, best_time_to_visit: e.target.value }))}
              margin="dense"
              placeholder="e.g., Spring, Summer 2025"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.title?.trim()}
        >
          {loading ? <CircularProgress size={20} /> : 'Update Dream'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDreamDialog;
